import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { SessionProvider } from '@/context/SessionContext';
import { HelmetProvider } from 'react-helmet-async';
import { AdminAuthProvider } from '@/context/AdminAuthContext';

// Import layout components
import { PublicLayout } from '@/components/layouts/PublicLayout.tsx';

// Lazy load the admin section
const AdminApp = lazy(() => import('@/components/admin/AdminApp.tsx'));

// Import the Cookie Banner
import { CookieBanner } from '@/components/CookieBanner.tsx';

// Import page components
import { TemporaryLayout } from '@/components/layouts/TemporaryLayout.tsx';
import { LandingPage } from '@/components/LandingPage.tsx';
// import { TemporaryLandingPage } from '@/components/TemporaryLandingPage.tsx';
import { AssessmentPage } from '@/components/AssessmentPage.tsx';
import { ContactPage } from '@/components/ContactPage';
import { AboutPage } from '@/components/AboutPage';
import { MaintenanceSignsPage } from "@/components/MaintenanceSignsPage.tsx";
import { MethodologyPage } from "@/components/MethodologyPage.tsx";
import { ServicesPage } from "@/components/ServicesPage.tsx";
import { TermsOfServicePage } from '@/components/TermsOfServicePage.tsx';
import { PrivacyPolicyPage } from '@/components/PrivacyPolicyPage.tsx';
import { CookiePolicyPage } from '@/components/CookiePolicyPage.tsx';

import { ScrollToTop } from './components/ScrollToTop';

import './index.css';

function App() {
    return (
        <HelmetProvider>
            <SessionProvider>
                <AdminAuthProvider>
                    <Router>
                        <ScrollToTop />
                        <Routes>
                            {/* Public temporary page - no header/footer */}
                            {/*<Route path="/" element={*/}
                            {/*    <TemporaryLayout>*/}
                            {/*        <TemporaryLandingPage />*/}
                            {/*    </TemporaryLayout>*/}
                            {/*} />*/}
                            {/* Full site behind /temp route */}
                            <Route path="/*" element={
                                <PublicLayout>
                                    <Routes>
                                        <Route path="/" element={<LandingPage />} />
                                        <Route path="/assessment" element={<AssessmentPage />} />
                                        <Route path="/contact" element={<ContactPage />} />
                                        <Route path="/about" element={<AboutPage />} />
                                        <Route path="/methodology" element={<MethodologyPage />} />
                                        <Route path="/services" element={<ServicesPage />} />
                                        <Route path="/5-signs-maintenance-strategy" element={<MaintenanceSignsPage />} />
                                        <Route path="/privacy" element={<PrivacyPolicyPage />} />
                                        <Route path="/terms" element={<TermsOfServicePage />} />
                                        <Route path="/cookies" element={<CookiePolicyPage />} />
                                        <Route path="*" element={<NotFoundPage />} />
                                    </Routes>
                                    {/* Cookie Banner appears on all public pages */}
                                    <CookieBanner />
                                </PublicLayout>
                            } />

                            {/* Admin Routes */}
                            <Route path="/admin/*" element={
                                <Suspense fallback={
                                    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                            <p className="text-gray-600">Loading admin panel...</p>
                                        </div>
                                    </div>
                                }>
                                    <AdminApp />
                                </Suspense>
                            } />

                            {/* Global 404 - must be last */}
                            <Route path="*" element={
                                <TemporaryLayout>
                                    <NotFoundPage />
                                </TemporaryLayout>
                            } />
                        </Routes>
                    </Router>
                </AdminAuthProvider>
            </SessionProvider>
        </HelmetProvider>
    );
}

export default App;

const NotFoundPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
                <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                <a
                    href="/"
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                >
                    Return Home
                </a>
            </div>
        </div>
    );
};